// src/features/dashboard/components/EmployeeStatsSection.tsx

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Clock3,
  TimerReset,
  Zap,
  Users,
  Triangle,
} from 'lucide-react';

import { useEmployeeStats } from '../hooks/useEmployeeStats';
import { useLoaderContext } from '../../../components/Loader/LoaderContext';

/* ============================================================================
   TIME FORMATTER
   ============================================================================ */

/**
 * Converts time values into:
 *
 * 7 h 12 m
 * 8 h 34 m
 * 45 m
 *
 * Supported API formats:
 *
 * "07:12:00" -> "7 h 12 m"
 * "07:12"    -> "7 h 12 m"
 * "00:45:00" -> "45 m"
 * "08:00:00" -> "8 h"
 *
 * Also supports decimal-hour numbers:
 *
 * 7.2  -> "7 h 12 m"
 * 8.57 -> "8 h 34 m"
 */
const formatDuration = (
  value?: string | number | null,
): string => {
  if (
    value === null ||
    value === undefined ||
    value === ''
  ) {
    return '--';
  }

  /* --------------------------------------------------------------------------
     NUMBER VALUE
     -------------------------------------------------------------------------- */

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return '--';
    }

    return decimalHoursToDuration(value);
  }

  let timeValue = String(value).trim();

  if (!timeValue) {
    return '--';
  }

  /* --------------------------------------------------------------------------
     HANDLE + / - SIGN
     -------------------------------------------------------------------------- */

  let sign = '';

  if (timeValue.startsWith('+')) {
    sign = '+';
    timeValue = timeValue.substring(1);
  } else if (timeValue.startsWith('-')) {
    sign = '-';
    timeValue = timeValue.substring(1);
  }

  /* --------------------------------------------------------------------------
     HH:mm:ss OR HH:mm
     -------------------------------------------------------------------------- */

  if (timeValue.includes(':')) {
    const parts = timeValue
      .split(':')
      .map((part) => Number(part));

    if (parts.length === 3) {
      const [
        hours,
        minutes,
        seconds,
      ] = parts;

      if (
        !Number.isFinite(hours) ||
        !Number.isFinite(minutes) ||
        !Number.isFinite(seconds)
      ) {
        return '--';
      }

      /*
       * Seconds are intentionally ignored
       * because the UI requires HH h MM m.
       */
      return formatHoursAndMinutes(
        sign,
        hours,
        minutes,
      );
    }

    if (parts.length === 2) {
      const [
        hours,
        minutes,
      ] = parts;

      if (
        !Number.isFinite(hours) ||
        !Number.isFinite(minutes)
      ) {
        return '--';
      }

      return formatHoursAndMinutes(
        sign,
        hours,
        minutes,
      );
    }

    return '--';
  }

  /* --------------------------------------------------------------------------
     NUMERIC STRING
     -------------------------------------------------------------------------- */

  const numericValue =
    Number(timeValue);

  if (Number.isFinite(numericValue)) {
    return decimalHoursToDuration(
      numericValue,
      sign,
    );
  }

  return timeValue;
};

/* ============================================================================
   FORMAT HH + MM
   ============================================================================ */

const formatHoursAndMinutes = (
  sign: string,
  hours: number,
  minutes: number,
): string => {
  /*
   * Normalize minutes if API happens to
   * return something greater than 59.
   */
  const normalizedHours =
    hours +
    Math.floor(minutes / 60);

  const normalizedMinutes =
    minutes % 60;

  /* ----------------------------------------------------------
     Hours + minutes
     ---------------------------------------------------------- */

  if (
    normalizedHours > 0 &&
    normalizedMinutes > 0
  ) {
    return `${sign}${normalizedHours} h ${normalizedMinutes} m`;
  }

  /* ----------------------------------------------------------
     Hours only
     ---------------------------------------------------------- */

  if (normalizedHours > 0) {
    return `${sign}${normalizedHours} h`;
  }

  /* ----------------------------------------------------------
     Minutes only
     ---------------------------------------------------------- */

  return `${sign}${normalizedMinutes} m`;
};

/* ============================================================================
   DECIMAL HOURS → HH h MM m
   ============================================================================ */

const decimalHoursToDuration = (
  decimalHours: number,
  explicitSign = '',
): string => {
  if (!Number.isFinite(decimalHours)) {
    return '--';
  }

  let sign = explicitSign;

  if (!sign && decimalHours < 0) {
    sign = '-';
  }

  const absoluteHours =
    Math.abs(decimalHours);

  let hours =
    Math.floor(absoluteHours);

  let minutes =
    Math.round(
      (absoluteHours - hours) * 60,
    );

  /*
   * Prevent:
   *
   * 7 h 60 m
   *
   * from appearing.
   */
  if (minutes === 60) {
    hours += 1;
    minutes = 0;
  }

  if (
    hours > 0 &&
    minutes > 0
  ) {
    return `${sign}${hours} h ${minutes} m`;
  }

  if (hours > 0) {
    return `${sign}${hours} h`;
  }

  return `${sign}${minutes} m`;
};

/* ============================================================================
   COMPONENT
   ============================================================================ */

const EmployeeStatsSection = () => {
  const { hideLoader } =
    useLoaderContext();

  const navigate =
    useNavigate();

  const {
    data,
    loading,
    error,
  } = useEmployeeStats();

  /* =========================================================================
     LOADER
     ========================================================================= */

  useEffect(() => {
    if (!loading) {
      hideLoader();
    }
  }, [
    loading,
    hideLoader,
  ]);

  /* =========================================================================
     LOADING
     ========================================================================= */

  if (loading) {
    return (
      <div
        className="
          col-span-4
          rounded-[18px]
          border
          border-[#D0D5DD]
          bg-white
          p-4
        "
      >
        Loading...
      </div>
    );
  }

  /* =========================================================================
     ERROR
     ========================================================================= */

  if (error) {
    return (
      <div
        className="
          col-span-4
          rounded-[18px]
          border
          border-red-200
          bg-white
          p-4
          text-sm
          text-red-500
        "
      >
        {error}
      </div>
    );
  }

  /* =========================================================================
     NO DATA
     ========================================================================= */

  if (!data) {
    return null;
  }

  /* =========================================================================
     STATISTICS
     ========================================================================= */

  const stats = [
    /* ------------------------------------------------------------------------
       LOGGED HOURS
       ------------------------------------------------------------------------ */

    {
      title: 'Logged Hours',

      route:
        '/employee-logged-hours-report',

      value:
        formatDuration(
          data.avgLoggedHours,
        ),

      sub:
        `${data.loggedTrendPercentage}%`,

      icon: Clock3,

      bg: 'bg-[#4285F4]',

      subColor:
        data.loggedTrendDirection === 'up'
          ? 'text-[#0A7D35]'
          : 'text-[#667085]',

      showArrow:
        data.loggedTrendDirection === 'up',

      showMinus:
        data.loggedTrendDirection === 'same',
    },

    /* ------------------------------------------------------------------------
       ACTIVE HOURS
       ------------------------------------------------------------------------ */

    {
      title: 'Active Hours',

      route:
        '/employee-active-hours',

      value:
        formatDuration(
          data.avgActiveHours,
        ),

      sub:
        `${data.activeTrendPercentage}%`,

      icon: Users,

      bg: 'bg-[#8B5CF6]',

      subColor:
        data.activeTrendDirection === 'up'
          ? 'text-[#0A7D35]'
          : 'text-[#667085]',

      showArrow:
        data.activeTrendDirection === 'up',

      showMinus:
        data.activeTrendDirection === 'same',
    },

    /* ------------------------------------------------------------------------
       IDLE TIME
       ------------------------------------------------------------------------ */

    {
      title: 'Idle Time',

      route:
        '/employee-idle-time',

      value:
        formatDuration(
          data.avgIdleHours,
        ),

      sub:
        `${data.idleTrendPercentage}%`,

      icon: TimerReset,

      bg: 'bg-[#F26B2D]',

      subColor:
        data.idleTrendDirection === 'up'
          ? 'text-[#D92D20]'
          : 'text-[#155EEF]',

      showArrow:
        data.idleTrendDirection === 'up',

      showMinus:
        data.idleTrendDirection === 'same',
    },

    /* ------------------------------------------------------------------------
       PRODUCTIVITY
       ------------------------------------------------------------------------ */

    {
      title: 'Productivity',

      route:
        '/emp-productivity-report',

      value:
        String(
          data.avgProductivity,
        ),

      unit1: '%',

      sub:
        `${data.productivityTrendPercentage}% · target ${data.dailyProductivityPercentageTarget}%`,

      icon: Zap,

      bg: 'bg-[#36B37E]',

      subColor:
        data.productivityTrendDirection === 'up'
          ? 'text-[#0A7D35]'
          : 'text-[#667085]',

      showArrow:
        data.productivityTrendDirection === 'up',

      showMinus:
        data.productivityTrendDirection === 'same',
    },
  ];

  /* =========================================================================
     RENDER
     ========================================================================= */

  return (
    <div
      className="
        grid
        w-full
        grid-cols-4
        gap-[14px]
      "
    >
      {stats.map((item) => {
        const Icon = item.icon;

        return (
          <div
            key={item.title}
            onClick={() =>
              navigate(item.route)
            }
            className="
              h-[122px]
              min-w-0
              w-full
              cursor-pointer
              overflow-hidden
              rounded-[18px]
              border
              border-[#D0D5DD]
              bg-white
              px-[16px]
              pt-[14px]
              pb-[10px]
              transition-all
              duration-200
              hover:border-[#2563EB]
              hover:shadow-md
            "
          >
            {/* ===============================================================
                HEADER
                =============================================================== */}

            <div
              className="
                flex
                min-w-0
                items-center
                gap-[8px]
              "
            >
              {/* Icon */}

              <div
                className={`
                  flex
                  h-[30px]
                  w-[30px]
                  min-h-[30px]
                  min-w-[30px]
                  shrink-0
                  items-center
                  justify-center
                  rounded-full
                  ${item.bg}
                `}
              >
                <Icon
                  size={12}
                  strokeWidth={2.3}
                  className="text-white"
                />
              </div>

              {/* Title */}

              <h3
                className="
                  min-w-0
                  flex-1
                  truncate
                  whitespace-nowrap
                  text-[12px]
                  font-semibold
                  text-[#101828]
                "
                title={item.title}
              >
                {item.title}
              </h3>
            </div>

            {/* ===============================================================
                VALUE
                =============================================================== */}

            <div
              className="
                mt-[10px]
                min-w-0
              "
            >
              <div
                className="
                  flex
                  min-w-0
                  items-end
                  leading-none
                "
              >
                <span
                  className="
                    min-w-0
                    truncate
                    whitespace-nowrap
                    text-[17px]
                    font-bold
                    tracking-[-0.3px]
                    text-black
                  "
                  title={item.value}
                >
                  {item.value}
                </span>

                {item.unit1 && (
                  <span
                    className="
                      ml-[2px]
                      mb-[2px]
                      shrink-0
                      whitespace-nowrap
                      text-[10px]
                      font-semibold
                      text-[#101828]
                    "
                  >
                    {item.unit1}
                  </span>
                )}
              </div>

              {/* =============================================================
                  FOOTER
                  ============================================================= */}

              <div
                className={`
                  mt-[13px]
                  flex
                  min-w-0
                  items-center
                  gap-[3px]
                  overflow-hidden
                  whitespace-nowrap
                  text-[10px]
                  font-medium
                  leading-[13px]
                  ${item.subColor}
                `}
              >
                {/* Up indicator */}

                {item.showArrow && (
                  <Triangle
                    size={7}
                    fill="currentColor"
                    strokeWidth={0}
                    className="shrink-0"
                  />
                )}

                {/* Same indicator */}

                {item.showMinus && (
                  <span
                    className="
                      shrink-0
                      text-[10px]
                      font-semibold
                    "
                  >
                    –
                  </span>
                )}

                {/* Text */}

                <span
                  className="
                    min-w-0
                    truncate
                  "
                  title={item.sub}
                >
                  {item.sub}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default EmployeeStatsSection;